import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { DEFAULT_LANGUAGE, DraggableMultiSelectMapper, FIRST_ELEMENT_INDEX } from '@modeso/aumico-lib-core-fe';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import {
    ComponentType,
    ComponentsOptionsDTO,
    IInputOptions,
    INotesTypeEntityDTO,
    IRichText,
    ISuperTableOptions,
    Options,
    UpdateNotesTypeDto,
} from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import { difference } from 'lodash-es';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { Observable } from 'rxjs';
import { IComponentOptionsDelegate } from '../../../components/componentOptions/IComponentOptionsDelegate';
import { ComponentOptionsComponent } from '../../../components/componentOptions/componentOptions.component';
import { SuperTableOptionsFormDialogComponent } from '../../../components/componentOptions/super-table-options/super-table-options.component';
import { InputOptionsDialogComponent } from '../../../components/input-options-dialog/input-options-dialog.component';
import { PreviewComponent } from '../../../components/preview/preview.component';
import { IRichTextDelegate } from '../../../components/richText/IRichTextDelegate.interface';
import { RichTextComponent } from '../../../components/richText/richText.component';
import { Errors } from '../../../enums/errors.enum';
import { NotesTypeFieldsName } from '../../../enums/notesTypeFieldsName.enum';
import { ComponentOptionsKeys } from '../../../helpers/componentOptionsKeys.helper';
import { NotesTypesHelper } from '../../../helpers/notesTypes.helper';
import { IComponentOptionsProperties } from '../../../interfaces/IComponentOptionsProperties.interface';
import { IInputOptionsDialogData } from '../../../interfaces/input-options-dialog-data.interface';
import { ISuperTableOptionsDialogData } from '../../../interfaces/super-table-options-dialog-data.interface';
import { NotesProvider } from '../../../provider/notes.provider';
import { DialogTypes } from '../../../types/dialog.types';
import { INotesTypesFormDelegate } from './INotesTypesFormDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesTypesFormPage');

@UntilDestroy()
@Component({
    selector: 'aumico-notes-types-form-page',
    templateUrl: 'notes-types-form.page.html',
    styleUrls: ['notes-types-form.page.scss'],
})
export class NotesTypesFormPage implements OnInit, IComponentOptionsDelegate, IRichTextDelegate {
    public defaultLanguage = DEFAULT_LANGUAGE;

    @Input() public set injectedProviderSetter(injectedProvider: NotesProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Input() languages: string[] = ['en-us', 'de-ch'];
    @Input() currentLanguage = 'de-ch';

    DELEGATE?: INotesTypesFormDelegate;
    @Input() public set delegate(delegate: INotesTypesFormDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    public errorMessage: Observable<string>;
    private deletedSelectionOptionsIndices: number[];

    data: INotesTypeEntityDTO;
    form: UntypedFormGroup;
    componentOptions: Omit<ComponentsOptionsDTO, 'componentName'>;
    componentsData: Map<string, Omit<ComponentsOptionsDTO, 'componentName'>>;
    currentDifference: INameId;
    isNameExist = false;
    isBackendReturnError = false;
    backendErrors: string;
    toolbarOptions: QuillModules;
    showInformationEditorErrors = false;

    readonly components: INameId[] = this.notesTypesHelper.getArrayOfINameIds(Object.values(ComponentType));
    readonly fieldsName = NotesTypeFieldsName;

    selectedComponents: INameId[] = [];
    selectionNotesFromBackend: INameId[] = [];

    readonly nameExistenceError = Errors.NameExistenceError;
    readonly nameMinLength = Errors.MinNameLengthIs_5;
    readonly mandatoryError = Errors.MandatoryFieldError;

    @ViewChild('componentsSelection', { static: false }) componentsSelection: MatSelect;
    @ViewChild('preview', { static: false }) preview: PreviewComponent;

    @ViewChildren('informationEditor') informationEditor: QueryList<QuillEditorComponent>;

    constructor(
        private provider: NotesProvider,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private componentOptionsKeys: ComponentOptionsKeys,
        private notesTypesHelper: NotesTypesHelper,
        private draggableMultiSelectMapper: DraggableMultiSelectMapper,
    ) {
        this.componentsData = new Map<string, Omit<ComponentsOptionsDTO, 'componentName'>>();
    }

    ngOnInit(): void {
        this.prepareToolbarOptions();
        this.provider.getActiveNotesType$().pipe(untilDestroyed(this)).subscribe(this.handleGetNotesType.bind(this));
        this.initNotesForm();
    }

    handleGetNotesType(notesType: INotesTypeEntityDTO) {
        if (notesType) {
            this.data = notesType;
            this.componentsData = this.notesTypesHelper.getComponentOptions(this.data.componentsOptions);

            const notesComponents = notesType.componentsOptions.map((component) => component.componentName);
            this.selectionNotesFromBackend = this.notesTypesHelper.getArrayOfINameIds(notesComponents);
            this.selectedComponents = this.draggableMultiSelectMapper.getSelectedOptionsOfSelectionOptions(
                this.components,
                this.selectionNotesFromBackend,
            );

            debug('ngOnInit getActiveNotesType result');
            debug(notesType);
        }
    }

    initNotesForm() {
        const formGroup = {};
        formGroup[this.fieldsName.NAME] = new UntypedFormControl(
            { value: this.data ? this.data.name : null, disabled: false },
            {
                validators: [Validators.required, Validators.minLength(5)],
            },
        );

        // title
        formGroup[`${this.fieldsName.TITLE}-${DEFAULT_LANGUAGE}`] = new UntypedFormControl(
            {
                value: this.data ? this.data.title[DEFAULT_LANGUAGE] : null,
                disabled: false,
            },
            { validators: [Validators.required] },
        );

        formGroup[this.fieldsName.NOTES_COMPONENTS] = new UntypedFormControl(
            {
                value: this.data ? this.selectedComponents : null,
                disabled: false,
            },
            { validators: [Validators.required] },
        );

        // information
        const informationByLanguage = this.data ? this.data.information[DEFAULT_LANGUAGE] : null;
        formGroup[`${this.fieldsName.INFORMATION}-${DEFAULT_LANGUAGE}`] = new UntypedFormControl(
            informationByLanguage,
            {
                validators: [Validators.required],
            },
        );

        debug('formGroup');
        debug(formGroup);
        this.form = this.formBuilder.group(formGroup);
    }

    private prepareToolbarOptions() {
        this.toolbarOptions = {
            toolbar: [
                [{ font: ['Arial', 'serif', 'monospace'] }],
                [{ size: [] }],
                ['bold', 'italic', 'underline', { color: [] }, { background: [] }],
                [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['clean', 'strike', { direction: 'rtl' }, 'blockquote'],
            ],
        };
    }

    get notesComponents() {
        return this.form.get(this.fieldsName.NOTES_COMPONENTS);
    }

    getCurrentSelectionAndDifferenceOfPreviousSelection(change: INameId[]) {
        if (change != null) {
            const selections = change;

            this.currentDifference = difference(selections, this.selectedComponents)[0];

            // if difference is undefined then a selection is removed
            if (this.currentDifference == null) {
                const removedComponent = difference(this.selectedComponents, change)[0];
                this.componentsData.delete(removedComponent.name);

                this.updatePreview();

                this.selectedComponents = change;
            } else {
                this.componentsSelection.close();
                const keys: DialogTypes = this.componentOptionsKeys.getKeysByComponentName(
                    this.currentDifference.name as ComponentType,
                    this.languages,
                    this.componentsData,
                );
                this.openComponentOptionAsDialogBySelectedComponent(keys, this.currentDifference, selections);
            }
        }
    }

    private openComponentOptionAsDialogBySelectedComponent(
        keys: DialogTypes,
        selectedComponent: INameId,
        updatedSelectedComponents: INameId[],
    ) {
        const selectedComponentName = selectedComponent.name;
        if (selectedComponentName === 'Rich text') {
            this.openRichTextAsDialog(keys as IRichText[], updatedSelectedComponents);
        } else if (selectedComponentName === 'Input') {
            this.openInputOptionsAsDialog(updatedSelectedComponents);
        } else if (selectedComponentName === 'Super Table') {
            this.openSuperTableOptionsAsDialog(updatedSelectedComponents);
        } else {
            this.openComponentOptions(
                keys as IComponentOptionsProperties,
                updatedSelectedComponents,
                selectedComponentName,
            );
        }
    }

    private openRichTextAsDialog(translatableText: IRichText[], updatedSelectedComponents: INameId[]) {
        const dialogRef = this.dialog.open(RichTextComponent, {
            width: '50%',
        });

        dialogRef.componentInstance.translatableTexts = translatableText;
        dialogRef.componentInstance.delegate = this;
        dialogRef.componentInstance.asDialog = true;
        dialogRef.componentInstance.hideReference = true;
        dialogRef.componentInstance.language = DEFAULT_LANGUAGE;
        dialogRef.componentInstance.componentIndex = this.getComponentIndex();

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.updateSelectedComponentsAndComponentsData(updatedSelectedComponents);
                debug('The dialog was closed');
            });
    }

    private openInputOptionsAsDialog(updatedSelectedComponents: INameId[]): void {
        const dialogData: IInputOptionsDialogData = {
            options:
                this.componentsData != null && this.componentsData.get('Input') != null
                    ? (this.componentsData.get('Input')[FIRST_ELEMENT_INDEX] as IInputOptions)
                    : undefined,
            languages: this.languages,
        };
        const dialogRef = this.dialog.open(InputOptionsDialogComponent, {
            width: '30%',
            data: dialogData,
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((options: IInputOptions | undefined) => {
                if (options == null) {
                    this.componentsSelection.writeValue(this.selectedComponents);
                    return;
                }

                const index = this.getComponentIndex();
                this.componentOptions = { componentId: `${ComponentType.INPUT}_${index}`, componentOptions: [options] };
                this.updateSelectedComponentsAndComponentsData(updatedSelectedComponents);
            });
    }

    private openSuperTableOptionsAsDialog(updatedSelectedComponents: INameId[]): void {
        const dialogData: ISuperTableOptionsDialogData = {
            options:
                this.componentsData != null && this.componentsData.get('Super Table') != null
                    ? (this.componentsData.get('Super Table')[FIRST_ELEMENT_INDEX] as ISuperTableOptions)
                    : undefined,
            languages: this.languages,
        };
        const dialogRef = this.dialog.open(SuperTableOptionsFormDialogComponent, {
            width: '750px',
            maxHeight: '650px',
            panelClass: 'super-table-options-container',
            data: dialogData,
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((options: ISuperTableOptions | undefined) => {
                if (options == null) {
                    this.componentsSelection.writeValue(this.selectedComponents);
                    return;
                }

                const index = this.getComponentIndex();
                this.componentOptions = {
                    componentId: `${ComponentType.SUPER_TABLE}_${index}`,
                    componentOptions: [options],
                };

                this.updateSelectedComponentsAndComponentsData(updatedSelectedComponents);
            });
    }

    private openComponentOptions(
        keys: IComponentOptionsProperties,
        updatedSelectedComponents: INameId[],
        selectedComponent: string,
    ) {
        const dialogRef = this.dialog.open(ComponentOptionsComponent, {
            width: '50%',
            height: '70%',
        });

        dialogRef.componentInstance.formFields = keys;
        dialogRef.componentInstance.languages = [DEFAULT_LANGUAGE];
        dialogRef.componentInstance.delegate = this;
        dialogRef.componentInstance.componentId = `${selectedComponent}_${this.getComponentIndex()}`;

        dialogRef.componentInstance.removeSelectionOption
            .pipe(untilDestroyed(this))
            .subscribe((deletedSelectionOptionsIndices: number[]) => {
                this.deletedSelectionOptionsIndices = deletedSelectionOptionsIndices;
            });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                // if result value is "empty" it means the user press on the close button
                if (!(result === 'empty')) {
                    this.updateSelectedComponentsAndComponentsData(updatedSelectedComponents);
                }
                // if user close the dialog without press on save, unselect the value
                this.componentsSelection.writeValue(this.selectedComponents);

                debug('The dialog was closed');
            });
    }

    private updateSelectedComponentsAndComponentsData(updatedSelectedComponents: INameId[]) {
        this.componentsData.set(this.currentDifference.name, this.componentOptions);

        this.selectedComponents = [...updatedSelectedComponents];
        this.updatePreview();
    }

    savedOptions(options: Options, componentId: string) {
        debug('options content');
        debug(options);
        this.componentOptions = { componentOptions: [options], componentId };
    }

    richTextContent(content: IRichText[], componentIndex: number) {
        debug('rich text content');
        debug(content);
        const componentId = `${ComponentType.RICH_TEXT}_${componentIndex}`;
        this.componentOptions = { componentOptions: content, componentId };
    }

    submit() {
        debug('in submit');
        debug(this.form.value);
        if (!this.form.valid) {
            return;
        }

        const notesTypesFormValues: INotesTypeEntityDTO = this.notesTypesHelper.getNotesTypeFormValues(
            this.form.value,
            this.componentsData,
        );
        debug('notes type request');
        debug(notesTypesFormValues);

        // TODO: If owner couldn't be known in frontend this check should be removed otherwise will apply the check with the known owner
        // this.checkPreconditions(notesTypesFormValues.name, notesTypesFormValues.owner);

        this.sendRequest(notesTypesFormValues);
    }

    private sendRequest(notesType: INotesTypeEntityDTO) {
        if (this.data && !this.isNameExist) {
            notesType._id = this.data._id;
            let deletedSelectionOptionsIndices: number[] = [];
            notesType.componentsOptions.forEach((component) => {
                if (component.componentName === ComponentType.SELECT) {
                    deletedSelectionOptionsIndices = this.deletedSelectionOptionsIndices;
                }
            });
            const updateNotesTypeDto: UpdateNotesTypeDto = {
                noteType: notesType,
                deletedSelectionOptionsIndexes: deletedSelectionOptionsIndices,
            };
            this.provider.editNotesType(updateNotesTypeDto);
            this.DELEGATE.afterNotesTypeSaved();
        }

        if (!this.data && !this.isNameExist) {
            this.provider.createNotesType(notesType);
            this.DELEGATE.afterNotesTypeSaved();
        }
    }

    getPropertyName(propertyName: string, language: string): string {
        return `${propertyName}-${language}`;
    }

    getSelectedComponentsNames(selections: INameId[]): string[] {
        return selections.map((selectedNoteComponent: INameId) => selectedNoteComponent.name);
    }

    onCancel() {
        this.DELEGATE.cancelNotesType();
    }

    private getComponentIndex(): number {
        return this.selectedComponents.length;
    }

    private updatePreview(): void {
        if (this.preview != null) {
            this.preview.updatePreviewComponentOptions(this.componentsData);
        }
    }
}
